import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { IncidentSymbol } from "./incidentSymbols";

// =====================================================================
// MatchLineup — Substitution detail card (design handoff board L12).
//
// Pure presentation, fully props-driven: the opener (a pitch node's
// out-arrow or a bench row's in-arrow) passes the already-formatted
// substitution rows plus its own DOM anchor. Two subs on one player
// (in-then-off, L12.4) stack as two rows in ONE card, each row keeping
// its own minute; the header then joins the minutes with " · ".
//
// Desktop: anchored popover that NEVER covers its anchor — it prefers
// the side (right, then left) at the anchor's height and falls back to
// below/above, clamped to the viewport (L12.1: flips to the inner side
// near an edge). Mobile (<768px): bottom-anchored sheet over a light
// scrim; tap outside dismisses (L12.3 — the scrim is translucent so the
// tapped node stays visible).
//
// NO reason field — the provider has none (brief §2, decided).
// Minutes arrive as display strings ("62'", "45+2'") — the container
// formats minute+addtime; this card never re-derives them.
// Both players are ≥44px click targets navigating to the profile via
// platform_player_id (existing /search/player/:id route); a player
// without platform_player_id is not interactive (no dead affordance).
// =====================================================================

const CARD_WIDTH = 270;
const GAP = 12; // distance card ↔ anchor
const MARGIN = 8; // minimum distance to the viewport edge

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const isSheetViewport = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(max-width: 767px)").matches;

// Photo circle with the handoff's shirt-number fallback (L9): a missing
// or broken photo renders the number in the circle — never a broken
// image, never a placeholder glyph. Border colour carries no direction
// (slate = out, blue = in comes from arrow + border per the index board).
const PlayerPhoto = ({ player, dir }) => {
  const src = (player && player.logo) || null;
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    setErrored(false);
  }, [src]);
  const borderColor = dir === "in" ? "#2183FE" : "#64748B";
  const numberColor = dir === "in" ? "#0B5FCB" : "#475569";
  if (src && !errored) {
    return (
      <img
        src={src}
        alt=""
        onError={() => setErrored(true)}
        className="rounded-full object-cover flex-none"
        style={{ width: 30, height: 30, border: `2px solid ${borderColor}` }}
      />
    );
  }
  const number =
    player && player.shirt_number != null && player.shirt_number !== ""
      ? player.shirt_number
      : null;
  return (
    <div
      className="rounded-full flex-none flex items-center justify-center"
      style={{
        width: 30,
        height: 30,
        background: "#EDF1F6",
        border: `2px solid ${borderColor}`,
        fontSize: 11,
        fontWeight: 800,
        color: numberColor,
      }}
    >
      {number}
    </div>
  );
};

// One half of a row: photo, name, arrow + #number. The whole block is
// the click target (≥44px tall via minHeight).
const PlayerSide = ({ player, dir, onActivate }) => {
  const interactive = !!onActivate;
  const arrowColor = dir === "in" ? "#2183FE" : "#64748B";
  const nav = interactive
    ? {
        role: "button",
        tabIndex: 0,
        onClick: (event) => {
          event.stopPropagation();
          onActivate();
        },
        onKeyDown: (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.stopPropagation();
            onActivate();
          }
        },
      }
    : {};
  return (
    <div
      {...nav}
      className={`flex items-center min-w-0 ${
        interactive ? "cursor-pointer hover:opacity-70" : ""
      }`}
      style={{ gap: 7, flex: "1 1 0", minHeight: 44 }}
    >
      <PlayerPhoto player={player} dir={dir} />
      <div className="min-w-0 flex flex-col">
        <div
          className="truncate"
          data-no-translate="true"
          style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}
        >
          {(player && player.name) || (player && player.first) || null}
        </div>
        <div className="flex items-center" style={{ gap: 4 }}>
          <span className="flex">
            <IncidentSymbol type={dir} size={14} />
          </span>
          {player && player.shirt_number != null && player.shirt_number !== "" ? (
            <span
              data-no-translate="true"
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: arrowColor,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              #{player.shirt_number}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const SubstitutionCard = ({
  open,
  rows = [],
  anchorEl = null,
  onClose,
  onPlayerClick,
  onMouseEnter,
  onMouseLeave,
  forceSheet = false,
}) => {
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [pos, setPos] = useState(null);
  const sheet = forceSheet || isSheetViewport();

  // Escape closes (both modes).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && onClose) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Desktop popover placement: side-first, never over the anchor,
  // clamped to the viewport; re-computed on scroll/resize while open.
  useLayoutEffect(() => {
    if (!open || sheet || !anchorEl) return undefined;
    const update = () => {
      const el = cardRef.current;
      if (!el || !anchorEl.getBoundingClientRect) return;
      const rect = anchorEl.getBoundingClientRect();
      const cardW = el.offsetWidth || CARD_WIDTH;
      const cardH = el.offsetHeight || 120;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const fitsRight = rect.right + GAP + cardW <= vw - MARGIN;
      const fitsLeft = rect.left - GAP - cardW >= MARGIN;
      let left;
      let top;
      if (fitsRight || fitsLeft) {
        // Beside the anchor at its own height (L12.1) — horizontally
        // disjoint from the anchor, so the vertical clamp can never
        // push the card over it.
        left = fitsRight ? rect.right + GAP : rect.left - GAP - cardW;
        top = clamp(rect.top + rect.height / 2 - cardH / 2, MARGIN, vh - cardH - MARGIN);
      } else {
        // No room on either side: drop below (L12.2), else above.
        left = clamp(rect.left + rect.width / 2 - cardW / 2, MARGIN, vw - cardW - MARGIN);
        const fitsBelow = rect.bottom + GAP + cardH <= vh - MARGIN;
        top = fitsBelow ? rect.bottom + GAP : Math.max(MARGIN, rect.top - GAP - cardH);
      }
      setPos({ left, top });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, sheet, anchorEl, rows.length]);

  if (!open || !rows.length) return null;

  const activate = (player) => {
    if (onPlayerClick) {
      onPlayerClick(player);
      return;
    }
    if (player && player.platform_player_id != null) {
      navigate(`/search/player/${player.platform_player_id}`);
    }
  };
  const canActivate = (player) =>
    !!player && (!!onPlayerClick || player.platform_player_id != null);

  const multi = rows.length > 1;
  const headMinute = multi
    ? rows.map((row) => row.minute).filter(Boolean).join(" · ")
    : rows[0].minute;

  const cardBody = (
    <div
      ref={cardRef}
      role="dialog"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={(event) => event.stopPropagation()}
      className="bg-white flex flex-col"
      style={{
        position: "fixed",
        zIndex: 61,
        ...(sheet
          ? { left: 12, right: 12, bottom: 12 }
          : {
              left: pos ? pos.left : -9999,
              top: pos ? pos.top : -9999,
              width: CARD_WIDTH,
              maxWidth: "calc(100vw - 16px)",
              visibility: pos ? "visible" : "hidden",
            }),
        border: "1px solid #E2E8F0",
        borderRadius: 12,
        boxShadow: "0 10px 28px rgba(15,23,42,0.16)",
        padding: 14,
        gap: 12,
      }}
    >
      <div className="flex items-baseline" style={{ gap: 8 }}>
        <div
          style={{
            fontSize: 24,
            fontWeight: 800,
            letterSpacing: "-0.02em",
            color: "#0F172A",
            lineHeight: 1,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {headMinute}
        </div>
        <div
          className="uppercase"
          style={{
            fontSize: 11,
            letterSpacing: "0.1em",
            color: "#94A3B8",
            fontWeight: 700,
          }}
        >
          Substitution
        </div>
      </div>

      {rows.map((row, index) => (
        <div key={index} className="flex flex-col" style={{ gap: 5 }}>
          {multi && row.minute ? (
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#64748B",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {row.minute}
            </div>
          ) : null}
          <div className="flex items-center" style={{ gap: 8 }}>
            <PlayerSide
              player={row.outPlayer}
              dir="out"
              onActivate={
                canActivate(row.outPlayer) ? () => activate(row.outPlayer) : null
              }
            />
            <div
              className="flex-none"
              aria-hidden="true"
              style={{ color: "#94A3B8", fontSize: 15, fontWeight: 700 }}
            >
              &rarr;
            </div>
            <PlayerSide
              player={row.inPlayer}
              dir="in"
              onActivate={
                canActivate(row.inPlayer) ? () => activate(row.inPlayer) : null
              }
            />
          </div>
        </div>
      ))}
    </div>
  );

  const content = sheet ? (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 60,
          background: "rgba(15,23,42,0.4)",
        }}
      />
      {cardBody}
    </>
  ) : (
    cardBody
  );

  return createPortal(content, document.body);
};

export default SubstitutionCard;
